import type { TokenValues } from "@silversea-ssc/dotcom-read-model-types/Tokens";
import {
  InclusionContent,
  InclusionDrawerSection,
  InclusionsDrawerContent,
  StepsGroup,
  useDrawer
} from "@silversea-ssc/ui-components-react";
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo
} from "react";
const InclusionsDrawer = React.lazy(
  () => import("components/fare/InclusionsDrawer/InclusionsDrawer")
);

type InclusionsDrawerContextValue = {
  inclusionsContent: InclusionContent[];
  onInclusionClick: (args: InfographicInfo) => void;
};

const InclusionsDrawerContext =
  React.createContext<InclusionsDrawerContextValue>({
    inclusionsContent: [] as InclusionContent[],
    onInclusionClick: () => {
      ///
    }
  });

type InfographicInfo = {
  title: string;
  inclusionsContentTokens: TokenValues | undefined;
  selectedStepGroupId: string;
  steps: StepsGroup[];
};

type InternalInfographcInfo = Omit<InfographicInfo, "activeInclusionCodes">;

type Props = {
  inclusionsContent: InclusionContent[];
};

export const InclusionsDrawerProvider: React.FC<PropsWithChildren<Props>> = ({
  children,
  inclusionsContent
}) => {
  const {
    closeDrawer,
    drawerData,
    isDrawerMountable,
    isDrowerOpen,
    openDrawer
  } = useDrawer<InternalInfographcInfo>(undefined);

  const onInclusionClick = useCallback(
    (args: InfographicInfo) => {
      openDrawer(args);
    },
    [openDrawer]
  );

  const contextValue = useMemo(() => {
    return {
      onInclusionClick,
      inclusionsContent
    };
  }, [onInclusionClick, inclusionsContent]);

  const selectedStepGroup = useMemo(
    () =>
      drawerData?.steps.find((x) => x.id == drawerData?.selectedStepGroupId),
    [drawerData]
  );

  return (
    <InclusionsDrawerContext.Provider value={contextValue}>
      {children}
      {isDrawerMountable && drawerData && (
        <React.Suspense>
          <InclusionsDrawer
            title={selectedStepGroup?.title}
            isOpen={isDrowerOpen}
            stepsGroup={selectedStepGroup}
            onRequestClose={closeDrawer}
          >
            <InclusionsDrawerContent>
              {selectedStepGroup?.steps
                .filter((s) => s.type == "active")
                .map((step) => (
                  <React.Fragment key={step.id}>
                    {step.inclusions.map((inclusion) => (
                      <InclusionDrawerSection
                        description={inclusion.description}
                        icon={inclusion.icon}
                        title={inclusion.title}
                        tokens={drawerData.inclusionsContentTokens}
                        key={inclusion._id}
                      />
                    ))}
                  </React.Fragment>
                ))}
            </InclusionsDrawerContent>
          </InclusionsDrawer>
        </React.Suspense>
      )}
    </InclusionsDrawerContext.Provider>
  );
};

export const useInclusionsDrawer = () => {
  return useContext(InclusionsDrawerContext);
};
